import type { BuildProgress } from '../../shared/types.js';
import { num, t } from '../i18n/i18n.js';

/**
 * The two clocks beside a world build: how long it has been going, and how long is left.
 *
 * The server's estimate arrives with each build step, and steps arrive once a map tile —
 * which on public Overpass can be forty seconds apart. So the estimate is turned into a
 * deadline when it arrives and counted down here in between. The same step also arrives
 * again unchanged whenever anything else about the lobby changes, or every time the wizard
 * polls; restarting the countdown from each of those would make it stand still, so only a
 * step that differs moves the deadline.
 */
export class BuildClock {
  private lastStep = '';
  private startedAt = 0;
  private deadline: number | null = null;

  constructor(private readonly now: () => number = () => performance.now()) {}

  /** Feeds a step in; null when there is no build, which resets both clocks. */
  update(step: Omit<BuildProgress, 'startedAt'> | null): void {
    if (!step) {
      this.lastStep = '';
      this.startedAt = 0;
      this.deadline = null;
      return;
    }
    if (!this.startedAt) this.startedAt = this.now();
    const key = JSON.stringify([step.stage, step.label, step.detail, step.fraction, step.etaSeconds]);
    if (key === this.lastStep) return;
    this.lastStep = key;
    this.deadline = step.etaSeconds === null ? null : this.now() + step.etaSeconds * 1000;
  }

  /** Since this client first saw the build — see `LobbyScreen.paintBuild` for why not the server's start. */
  elapsedSeconds(): number {
    return this.startedAt ? Math.floor((this.now() - this.startedAt) / 1000) : 0;
  }

  /** Seconds left, negative once overdue; null when there is no estimate. */
  remainingSeconds(): number | null {
    return this.deadline === null ? null : (this.deadline - this.now()) / 1000;
  }
}

/** An elapsed clock: "45s", "2m 05s". */
export function formatElapsed(seconds: number): string {
  if (seconds < 60) return t('{s}s', { s: seconds });
  return t('{m}m {s}s', { m: Math.floor(seconds / 60), s: String(seconds % 60).padStart(2, '0') });
}

/**
 * How long a wait will be, the way a person would say it: to the nearest five seconds under
 * a minute, the nearest half-minute above. An estimate quoted to the second claims a
 * precision the public Overpass servers do not have.
 */
export function formatWait(seconds: number): string {
  if (seconds < 60) return t('{s} seconds', { s: Math.max(5, Math.ceil(seconds / 5) * 5) });
  const minutes = Math.round(seconds / 30) / 2;
  return t('{m} min', { m: num(minutes, minutes % 1 ? 1 : 0) });
}

/** The line under the bar: what is left, or that it is overdue and still going. */
export function remainingPhrase(seconds: number | null): string {
  if (seconds === null) return '';
  // Overdue is not the same as stuck: a tile retried against a second server is still a
  // tile on its way, and saying so is what keeps somebody from reloading the page.
  if (seconds <= 0) return t('Taking longer than expected — the map server is busy. Still going.');
  if (seconds < 10) return t('Almost there');
  return t('About {time} left', { time: formatWait(seconds) });
}
